import {isPlatformBrowser, AsyncPipe} from '@angular/common';
import {ChangeDetectionStrategy, Component, ElementRef, Inject, OnInit, PLATFORM_ID, ViewChild} from '@angular/core';
import {DcpAuthService} from '@app/auth/services/dcp-auth.service';
import {DelegoPaymentErrorService} from '@app/payment/services/delego-rapidpay/error-handling/delego-payment-error.service';
import {DcpProductUtilitiesService} from '@app/product/services/dcp-product-utilities.service';
import {DcpCartDecoratorService} from '@app/services/dcp-cart-decorator.service';
import {TransactionTypeCode} from '@app/services/transactionTypes/transaction-type-code';
import {
  CommercialModelService,
  ConfigService,
  ConfiguratorUriParameterService,
  SupportDialogData,
} from '@dcp/configurator';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {ActiveCartFacade, OrderEntry} from '@spartacus/cart/base/root';

import {Product, User} from '@spartacus/core';
import {OrderDetailsService} from '@spartacus/order/components';
import {Order} from '@spartacus/order/root';

import {CurrentProductService} from '@spartacus/storefront';
import {UserAccountFacade} from '@spartacus/user/account/root';
import {combineLatest, forkJoin, Observable, of} from 'rxjs';
import {first, map, mergeMap, shareReplay, switchMap} from 'rxjs/operators';
import {SafeResourceUrlPipe} from '../../../pipes/safeResourceUrl/safeResourceUrl.pipe';
import {DcpSupportFormService} from '../dcp-support-form.service';

const CATEGORY_L1_UPSELL = 'upsell';
const CATEGORY_L1_CUSTOMER = 'appcenterCustomer';
const CATEGORY_L1_DIGITAL_NATIVE = 'digitalNative';
const CATEGORY_L2_DIGITAL_NATIVE = 'digitalNativeCheckout';
const CHECKOUT_URLS = ['/cart', '/checkout', '/order-confirmation'];

export interface SupportFormCategory {
  categoryL1?: string;
  categoryL2?: string;
}

export enum SupportFormSearchParameters {
  CUSTOMER_ID = 'customerID',
  USER_IDENTITY = 'userIdentity',
  EMAIL_ADDRESS = 'emailAddress',
  SOURCE = 'source',
  CART_ID = 'cartID',
  ERROR_CODE = 'errorCode',
  ERROR_MESSAGE = 'errorMessage',
  CATEGORY_L1 = 'categoryL1',
  CATEGORY_L2 = 'categoryL2',
}

export const DIGITAL_NATIVE_CHECKOUT_SUPPORT_FORM_CATEGORY: SupportFormCategory = {
  categoryL1: CATEGORY_L1_DIGITAL_NATIVE,
  categoryL2: CATEGORY_L2_DIGITAL_NATIVE,
};

export const UPSELL_CHECKOUT_SUPPORT_FORM_CATEGORY: SupportFormCategory = {
  categoryL1: CATEGORY_L1_UPSELL,
};

export const PARTNER_PDP_SUPPORT_FORM_CATEGORY: SupportFormCategory = {
  categoryL1: CATEGORY_L1_CUSTOMER,
};

export const SAP_PDP_SUPPORT_FORM_CATEGORY: SupportFormCategory = {
  categoryL1: CATEGORY_L1_DIGITAL_NATIVE,
};

@UntilDestroy()
@Component({
  selector: 'dcp-support-form',
  templateUrl: './support-form.component.html',
  styleUrls: ['./support-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AsyncPipe, SafeResourceUrlPipe],
})
export class SupportFormComponent implements OnInit {
  @ViewChild('iFrame') iframeComponent: ElementRef;
  baseUrl: string;

  /** Stable, single-subscription source for the iframe `[src]`, built once in {@link ngOnInit}. */
  url$: Observable<string>;

  user: User;
  activeCartID: string;
  errorCode: string;
  errorMessage: string;
  cartEntries: OrderEntry[] = [];
  private isProductDetailsPage: boolean;
  private isCartHasNetNewEntries: boolean;
  protected isUserLoggedIn: boolean;
  private isSapProduct: boolean;
  private srcUrl: URL;

  constructor(
    @Inject(PLATFORM_ID) private platformId: object,
    private userFacade: UserAccountFacade,
    private activeCartFacade: ActiveCartFacade,
    private dcpCartDecoratorService: DcpCartDecoratorService,
    private orderDetailsService: OrderDetailsService,
    protected dcpAuthService: DcpAuthService,
    private configuratorUriParameterService: ConfiguratorUriParameterService,
    private configService: ConfigService,
    private currentProductService: CurrentProductService,
    private dcpProductUtilitiesService: DcpProductUtilitiesService,
    private dcpSupportFormService: DcpSupportFormService,
    private delegoPaymentErrorService: DelegoPaymentErrorService,
    private commercialModelService: CommercialModelService,
  ) {
    this.baseUrl = this.configService.getString('supportForm.baseUrl');
  }

  ngOnInit(): void {
    this.currentProductService
      .getProduct()
      .pipe(first(), untilDestroyed(this))
      .subscribe((product: Product) => {
        this.isProductDetailsPage = !!product;
        if (this.isProductDetailsPage) {
          this.isSapProduct = this.dcpProductUtilitiesService.isSapProduct(product);
        }
      });
    this.url$ = this.dcpAuthService.isUserLoggedIn().pipe(
      first(),
      switchMap((isUserLoggedIn: boolean) => {
        this.isUserLoggedIn = isUserLoggedIn;
        return isUserLoggedIn ? this.prefillLoggedInUrl() : this.url;
      }),
      shareReplay({bufferSize: 1, refCount: true}),
    );
  }

  getURL(): Observable<string> {
    return this.isUserLoggedIn ? this.prefillLoggedInUrl() : this.url;
  }

  get url(): Observable<string> {
    return this.dcpSupportFormService.data$.pipe(
      map((data: SupportDialogData) => {
        if (data?.targetUrl && this.isValidUrl(data.targetUrl)) {
          const targetUrl = new URL(data.targetUrl);

          if (!targetUrl?.pathname?.includes('iframe')) {
            targetUrl.pathname = targetUrl.pathname.concat('/iframe');
          }

          return targetUrl.toString();
        }

        this.srcUrl = new URL(this.baseUrl);
        const supportFormCategory: SupportFormCategory = this.getSupportFormCategory();
        this.setSearchParam(SupportFormSearchParameters.CUSTOMER_ID, this.user?.orgUnit?.uid);
        this.setSearchParam(SupportFormSearchParameters.USER_IDENTITY, this.user?.scimUserId);
        this.setSearchParam(SupportFormSearchParameters.EMAIL_ADDRESS, this.user?.email);
        this.setSearchParam(SupportFormSearchParameters.SOURCE, decodeURIComponent(this.currentURL));
        this.setSearchParam(SupportFormSearchParameters.CART_ID, this.activeCartID);
        this.setSearchParam(SupportFormSearchParameters.ERROR_CODE, this.errorCode || data?.errorCode);
        this.setSearchParam(SupportFormSearchParameters.ERROR_MESSAGE, this.errorMessage || data?.errorMessage);
        this.setSearchParam(SupportFormSearchParameters.CATEGORY_L1, supportFormCategory?.categoryL1);
        this.setSearchParam(SupportFormSearchParameters.CATEGORY_L2, supportFormCategory?.categoryL2);

        const sourceUrl: URL = new URL(this.currentURL);
        sourceUrl.searchParams.forEach((value: string, key: string) => {
          if (!this.srcUrl.searchParams.has(key)) {
            this.srcUrl.searchParams.append(key, value);
          }
        });

        return this.srcUrl.toString();
      }),
    );
  }

  get currentURL(): string {
    return isPlatformBrowser(this.platformId) ? window.location.href : undefined;
  }

  private isValidUrl(url: string) {
    try {
      return new URL(url);
    } catch (_) {
      return false;
    }
  }

  private setSearchParam(parameter: string, value: string): void {
    if (value) {
      this.srcUrl.searchParams.set(parameter, value);
    }
  }

  /**
   * Checks if any entry in the cart is a net new transaction.
   *
   * @param entries list of order entries to check
   */
  private hasNetNewEntries(entries: OrderEntry[]): Observable<boolean> {
    if (!entries?.length) {
      return of(false);
    }

    const netNewChecks$ = entries.map((entry) => this.commercialModelService.isNetNewEntry(entry));
    return forkJoin(netNewChecks$).pipe(map((results) => results.some((isNetNew) => isNetNew)));
  }

  private prefillLoggedInUrl(): Observable<string> {
    return combineLatest([
      this.userFacade.get(),
      this.activeCartFacade.getActiveCartId(),
      this.dcpCartDecoratorService.priceDataSubject,
      this.activeCartFacade.getEntries(),
      this.orderDetailsService.getOrderDetails().pipe(map((order: Order) => order?.entries ?? [])),
      this.delegoPaymentErrorService.getErrorCodeAndMessage(),
      this.dcpSupportFormService.data$,
    ]).pipe(
      mergeMap(([user, activeCartID, pd, entries, orderEntries, errorAndMessage, data]) => {
        this.user = user;
        this.activeCartID = activeCartID;
        if (!data?.errorCode?.length && !data?.errorMessage?.length) {
          if (pd?.price) {
            this.errorCode = pd.price.errorCode;
            this.errorMessage = pd.price.errorMessage;
          } else {
            this.errorCode = errorAndMessage?.errorCode;
            this.errorMessage = errorAndMessage?.errorMessage;
          }
        }
        this.cartEntries = entries?.length ? entries : orderEntries;

        // Check if any entry is a net new transaction using CommercialModelService
        return this.hasNetNewEntries(this.cartEntries).pipe(
          switchMap((hasNetNew) => {
            this.isCartHasNetNewEntries = hasNetNew;
            return this.url;
          }),
        );
      }),
    );
  }

  private getSupportFormCategory(): SupportFormCategory {
    const action: string = this.configuratorUriParameterService.getAction();
    return this.isProductDetailsPage ? this.getPdpSupportFormCategory() : this.getActionSupportFormCategory(action);
  }

  private getPdpSupportFormCategory(): SupportFormCategory {
    return this.isSapProduct ? SAP_PDP_SUPPORT_FORM_CATEGORY : PARTNER_PDP_SUPPORT_FORM_CATEGORY;
  }

  private getActionSupportFormCategory(action: string): SupportFormCategory {
    return this.getDigitalNativeAction(action) || this.getOrderUpsellAction(action);
  }

  private isOrderUpsellAction(action: string): boolean {
    return (
      action?.includes(TransactionTypeCode.ORDER_UPSELL) ||
      (this.isCheckoutUrl() && this.cartEntries.length && !this.isCartHasNetNewEntries)
    );
  }

  private isDigitalNativeAction(action: string): boolean {
    return action?.includes(TransactionTypeCode.ORDER_NEW) || (this.isCheckoutUrl() && this.isCartHasNetNewEntries);
  }

  private getOrderUpsellAction(action: string): SupportFormCategory {
    return this.isOrderUpsellAction(action) ? UPSELL_CHECKOUT_SUPPORT_FORM_CATEGORY : undefined;
  }

  private getDigitalNativeAction(action: string): SupportFormCategory {
    return this.isDigitalNativeAction(action) ? DIGITAL_NATIVE_CHECKOUT_SUPPORT_FORM_CATEGORY : undefined;
  }

  private isCheckoutUrl(): boolean {
    return CHECKOUT_URLS.some((item: string) => this.currentURL?.includes(item));
  }
}
